import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const DEFAULT_DRAWER_WIDTH = 780;
const MIN_DRAWER_WIDTH = 520;
const MAX_DRAWER_WIDTH = 1200;
const DRAWER_VIEWPORT_GUTTER = 24;

export const drawerWidthBounds = () => {
  const viewportWidth = typeof window === "undefined"
    ? DEFAULT_DRAWER_WIDTH + DRAWER_VIEWPORT_GUTTER
    : window.innerWidth;
  const max = Math.max(
    320,
    Math.min(MAX_DRAWER_WIDTH, viewportWidth - DRAWER_VIEWPORT_GUTTER)
  );
  return { min: Math.min(MIN_DRAWER_WIDTH, max), max };
};

export const clampDrawerWidth = (width) => {
  const { min, max } = drawerWidthBounds();
  return Math.min(max, Math.max(min, Number(width) || DEFAULT_DRAWER_WIDTH));
};

const useResizableDrawer = (direction = "ltr") => {
  const [drawerWidth, setDrawerWidth] = useState(() =>
    clampDrawerWidth(DEFAULT_DRAWER_WIDTH)
  );
  const [drawerResizing, setDrawerResizing] = useState(false);
  const drawerResizeRef = useRef(null);

  const cancelDrawerResize = useCallback(() => {
    drawerResizeRef.current = null;
    document.body.classList.remove("account-drawer-is-resizing");
    setDrawerResizing(false);
  }, []);

  useEffect(() => () => {
    drawerResizeRef.current = null;
    document.body.classList.remove("account-drawer-is-resizing");
  }, []);

  useEffect(() => {
    const keepDrawerInViewport = () => {
      setDrawerWidth((current) => clampDrawerWidth(current));
    };
    window.addEventListener("resize", keepDrawerInViewport);
    return () => window.removeEventListener("resize", keepDrawerInViewport);
  }, []);

  const startDrawerResize = (event) => {
    if (window.innerWidth <= 767 || (event.button !== undefined && event.button !== 0)) {
      return;
    }
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // Pointer capture can fail if the browser has already cancelled the pointer.
    }
    drawerResizeRef.current = {
      startX: event.clientX,
      startWidth: drawerWidth,
      pointerId: event.pointerId
    };
    document.body.classList.add("account-drawer-is-resizing");
    setDrawerResizing(true);
  };

  const moveDrawerResize = (event) => {
    const session = drawerResizeRef.current;
    if (!session) return;
    const delta = direction === "rtl"
      ? event.clientX - session.startX
      : session.startX - event.clientX;
    setDrawerWidth(clampDrawerWidth(session.startWidth + delta));
  };

  const stopDrawerResize = (event) => {
    const session = drawerResizeRef.current;
    if (!session) return;
    try {
      if (event?.currentTarget?.hasPointerCapture?.(session.pointerId)) {
        event.currentTarget.releasePointerCapture(session.pointerId);
      }
    } catch {
      // The pointer may already have been released by the browser.
    }
    cancelDrawerResize();
  };

  const resizeDrawerWithKeyboard = (event) => {
    const { min, max } = drawerWidthBounds();
    let nextWidth;
    if (event.key === "Home") nextWidth = min;
    if (event.key === "End") nextWidth = max;
    if (event.key === "ArrowLeft") {
      nextWidth = drawerWidth + (direction === "rtl" ? -24 : 24);
    }
    if (event.key === "ArrowRight") {
      nextWidth = drawerWidth + (direction === "rtl" ? 24 : -24);
    }
    if (nextWidth === undefined) return;
    event.preventDefault();
    setDrawerWidth(clampDrawerWidth(nextWidth));
  };

  const resetDrawerWidth = () => {
    setDrawerWidth(clampDrawerWidth(DEFAULT_DRAWER_WIDTH));
  };

  const drawerStyle = useMemo(() => ({
    "--account-directory-drawer-width": `${drawerWidth}px`,
    "--bs-offcanvas-width": `${drawerWidth}px`,
    width: `${drawerWidth}px`
  }), [drawerWidth]);

  return {
    drawerBounds: drawerWidthBounds(),
    drawerResizing,
    drawerStyle,
    drawerWidth,
    cancelDrawerResize,
    moveDrawerResize,
    resetDrawerWidth,
    resizeDrawerWithKeyboard,
    startDrawerResize,
    stopDrawerResize
  };
};

export default useResizableDrawer;
